// Light/dark theme for the admin dashboard only — the public pages are never
// themed. The colours themselves live in src/styles.css under
// `html[data-admin-theme="dark"]`; this file decides when that attribute is set.
//
// A choice made with the toggle is remembered per browser; until then the
// dashboard follows the operating system's setting.

import { useCallback, useEffect, useState } from "react";

export type AdminTheme = "light" | "dark";

const STORAGE_KEY = "admin-theme";
const ATTRIBUTE = "data-admin-theme";

/**
 * Runs inline in <head> before the first paint (see src/routes/__root.tsx), so
 * an admin page never flashes light before turning dark. Self-contained on
 * purpose: it is serialised to a string and must not reference anything above.
 */
export const ADMIN_THEME_BOOT_SCRIPT = `(function(){try{if(location.pathname!=="/admin"&&location.pathname.indexOf("/admin/")!==0)return;var t=localStorage.getItem("${STORAGE_KEY}");if(t!=="light"&&t!=="dark")t=matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light";document.documentElement.setAttribute("${ATTRIBUTE}",t)}catch(e){}})()`;

function storedTheme(): AdminTheme | null {
	try {
		const value = localStorage.getItem(STORAGE_KEY);
		return value === "light" || value === "dark" ? value : null;
	} catch {
		// Storage can be blocked (private mode, site data disabled).
		return null;
	}
}

function systemTheme(): AdminTheme {
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

/**
 * Applies the theme while the admin layout is mounted and clears it on the way
 * out, so a client-side navigation to a public page lands back in light.
 * Returns the current theme and a toggle that remembers the choice.
 */
export function useAdminTheme() {
	// Unknown until mounted: the boot script has already painted the right
	// theme, and writing a guess to the page before reading the real one would
	// flash it. The server render (and that first frame) shows the light icon.
	const [theme, setTheme] = useState<AdminTheme | null>(null);

	useEffect(() => {
		setTheme(storedTheme() ?? systemTheme());

		// No explicit choice yet: keep following the OS when it switches.
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const onSystemChange = () => {
			if (!storedTheme()) setTheme(systemTheme());
		};
		media.addEventListener("change", onSystemChange);
		return () => {
			media.removeEventListener("change", onSystemChange);
			document.documentElement.removeAttribute(ATTRIBUTE);
		};
	}, []);

	useEffect(() => {
		if (theme) document.documentElement.setAttribute(ATTRIBUTE, theme);
	}, [theme]);

	const toggle = useCallback(() => {
		setTheme((current) => {
			const next = current === "dark" ? "light" : "dark";
			try {
				localStorage.setItem(STORAGE_KEY, next);
			} catch {
				// Still switches for this visit; it just won't be remembered.
			}
			return next;
		});
	}, []);

	return { theme: theme ?? "light", toggle };
}
